import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Card from "react-bootstrap/Card";
import Form from "react-bootstrap/Form";

import { Bar, Pie } from "react-chartjs-2";
import { ArcElement, BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Tooltip } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import CountUp from "react-countup";

import { getSadminDashData } from "../Store/Thunk";
import Overlay from "../../../SharedComponents/Overlay";

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, ChartDataLabels);

export default function SadminDash() {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const isLoading = useSelector((state) => state.sadminDash.isLoading);
  const dashData = useSelector((state) => state.sadminDash.dashData);

  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split("T")[0];
  };

  const getThirtyDaysAgo = () => {
    const pastDate = new Date();
    pastDate.setDate(pastDate.getDate() - 30);
    return pastDate.toISOString().split("T")[0];
  };

  const [startDate, setStartDate] = useState(getThirtyDaysAgo());
  const [endDate, setEndDate] = useState(getTomorrowDate());

  useEffect(() => {
    dispatch(getSadminDashData({ startDate, endDate }));
  }, [startDate, endDate, dispatch]);

  const SubmitHandler = (e) => {
    e.preventDefault();
    dispatch(getSadminDashData({ startDate, endDate })); // Optional, because useEffect already handles it
  };

  const calculateGrandTotals = () => {
    if (!dashData) return {};

    return dashData.reduce(
      (totals, company) => {
        totals.ticketCount += company.ticketCount;
        totals.candidateCount += company.candidateCount;
        totals.companyUserNumber += company.companyUserNumber;
        totals.sentApplicationsCount += company.sentApplicationsCount;
        totals.finishedApplicationsCount += company.finishedApplicationsCount;
        return totals;
      },
      {
        ticketCount: 0,
        candidateCount: 0,
        companyUserNumber: 0,
        sentApplicationsCount: 0,
        finishedApplicationsCount: 0,
      }
    );
  };

  const grandTotals = calculateGrandTotals();

  const pieData = {
    labels: ["Gönderilen Uygulamalar", "Biten Uygulamalar"],
    datasets: [
      {
        data: [grandTotals.sentApplicationsCount, grandTotals.finishedApplicationsCount],
        backgroundColor: ["#36A2EB", "#FF6384"],
        hoverBackgroundColor: ["#36A2EB", "#FF6384"],
      },
    ],
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      animateRotate: true,
      animateScale: true,
    },
    plugins: {
      datalabels: {
        formatter: (value, context) => {
          const total = context.chart.data.datasets[0].data.reduce((a, b) => a + b, 0);
          const percentage = ((value / total) * 100).toFixed(1) + "%";
          return percentage;
        },
        color: "#fff",
        font: {
          weight: "bold",
          size: 14,
        },
      },
    },
  };

  const companyNames = dashData?.map((c) => c.companyName) || [];
  const sentApplications = dashData?.map((c) => c.sentApplicationsCount) || [];
  const finishedApplications = dashData?.map((c) => c.finishedApplicationsCount) || [];

  const sentColors = sentApplications.map((sent, index) =>
    sent > finishedApplications[index] ? "#28a745" : "#dc3545"
  );
  const finishedColors = finishedApplications.map((finished, index) =>
    finished > sentApplications[index] ? "#28a745" : "#dc3545"
  );

  const barData = {
    labels: companyNames,
    datasets: [
      {
        label: "Gönderilen Uygulamalar",
        backgroundColor: sentColors,
        data: sentApplications,
      },
      {
        label: "Biten Uygulamalar",
        backgroundColor: finishedColors,
        data: finishedApplications,
      },
    ],
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y",
    plugins: {
      tooltip: {
        callbacks: {
          label: function (context) {
            const label = context.dataset.label || "";
            const value = context.parsed.x || 0;
            let comment = "";

            if (label.includes("Gönderilen")) {
              comment = value > 0 ? " (İyi Performans 👍)" : " (Geliştirilmeli ⚠️)";
            } else if (label.includes("Biten")) {
              comment = value > 0 ? " (Tamamlanmış ✅)" : " (Eksik ❗)";
            }

            return `${label}: ${value}${comment}`;
          },
        },
      },
    },
    scales: {
      x: { beginAtZero: true },
      y: { ticks: { autoSkip: false, maxRotation: 0, minRotation: 0 } },
    },
  };

  return (
    <div className="container mt-4">
      {isLoading && <Overlay />}

      <Row>
        <Col>
          <h1 className="mb-2">Hoşgeldin Paşam 👑</h1>
        </Col>
        <Col>
          <Card body className="mb-4 text-center">
            <h2>Şirket Sayısı: {dashData?.length ?? "-"}</h2>
          </Card>
        </Col>
      </Row>

      {/* Date Filter Form */}
      <Form onSubmit={SubmitHandler} className="mb-4">
        <Row className="align-items-end">
          <Col xs={12} md={5}>
            <Form.Label>Başlangıç Tarihi</Form.Label>
            <Form.Control type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </Col>
          <Col xs={12} md={5}>
            <Form.Label>Bitiş Tarihi</Form.Label>
            <Form.Control type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </Col>
          <Col xs={12} md={2} className="d-grid">
            <button type="submit" className="btn btn-primary">
              Filtrele
            </button>
          </Col>
        </Row>
      </Form>

      {/* Dashboard Stats */}
      <h3 className="mb-4">Toplam İstatistikler ve Uygulama Dağılımı</h3>
      <Row className="mb-4">
        <Col md={8}>
          <Row>
            {/* Stat Cards */}
            <Col md={6} className="mb-3">
              <Card bg="primary" text="white" className="text-center">
                <Card.Body>
                  <Card.Title>Kullanıcı Sayısı</Card.Title>
                  <Card.Text>
                    <CountUp end={grandTotals.companyUserNumber} duration={2} />
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} className="mb-3">
              <Card bg="success" text="white" className="text-center">
                <Card.Body>
                  <Card.Title>Toplam Aday</Card.Title>
                  <Card.Text>
                    <CountUp end={grandTotals.candidateCount} duration={2} />
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} className="mb-3">
              <Card bg="warning" text="white" className="text-center">
                <Card.Body>
                  <Card.Title>Toplam Ticket</Card.Title>
                  <Card.Text>
                    <CountUp end={grandTotals.ticketCount} duration={2} />
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} className="mb-3">
              <Card text="white" className="text-center" style={{ backgroundColor: "#36A2EB" }}>
                <Card.Body>
                  <Card.Title>Gönderilen Uygulamalar</Card.Title>
                  <Card.Text>
                    <CountUp end={grandTotals.sentApplicationsCount} duration={2} />
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={12} className="mb-3">
              <Card text="white" className="text-center" style={{ backgroundColor: "#FF6384" }}>
                <Card.Body>
                  <Card.Title>Biten Uygulamalar</Card.Title>
                  <Card.Text>
                    <CountUp end={grandTotals.finishedApplicationsCount} duration={2} />
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Col>

        {/* Pie Chart */}
        <Col md={4} className="d-flex mb-3">
          <Card body className="text-center w-100 d-flex flex-column justify-content-center h-100">
            <h5 >Uygulama Dağılımı</h5>
            <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <div style={{ width: "220px", height: "220px" }}>
                <Pie data={pieData} options={pieOptions} />
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Bar Chart */}
      <h3 className="mb-4">Şirket Başına Uygulama İstatistikleri (Yatay Bar)</h3>
      <Row className="mb-4">
        <Col>
          <Card body className="text-center">
            <div style={{ height: "500px" }}>
              <Bar data={barData} options={barOptions} />
            </div>
          </Card>
        </Col>
      </Row>

      {/* Company Table */}
      <h3 className="mb-4">Şirket Bazlı İstatistikler</h3>
      <Card className="mb-4 shadow-sm">
        <Card.Body>
          <div className="table-responsive">
            <table className="table table-striped table-bordered align-middle mb-0">
              <thead className="table-dark text-center">
                <tr>
                  <th>ID</th>
                  <th>Şirket Adı</th>
                  <th>Kullanıcı Sayısı</th>
                  <th>Toplam Aday</th>
                  <th>Toplam Ticket</th>
                  <th>Gönderilen Uygulamalar</th>
                  <th>Biten Uygulamalar</th>
                </tr>
              </thead>
              <tbody className="text-center">
                {dashData?.map((company, i) => (
                  <tr key={i}>
                    <td>{company.companyId}</td>
                    <td>{company.companyName}</td>
                    <td>{company.companyUserNumber}</td>
                    <td>{company.candidateCount}</td>
                    <td>{company.ticketCount}</td>
                    <td>{company.sentApplicationsCount}</td>
                    <td>{company.finishedApplicationsCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
}
